/**
 * Sistema de permisos del sistema FEPAST
 * Mapea los permisos de la base de datos a rutas y funcionalidades del frontend
 */

// Mapeo de permisos a rutas permitidas
export const PERMISSION_ROUTES = {
  'CRUD Socias': [
    '/partners',
    '/register-partner',
    '/edit-partner/:id',
    '/partner-detail/:id'
  ],
  'CRUD Hospitales': [
    '/hospitals'
  ],
  'CRUD Inventario': [
    '/inventory',
    '/ubicacion-inventario',
    '/categoria-inventario'
  ],
  'CRUD Categoria Inventario': [
    '/categoria-inventario'
  ],
  'CRUD Ubicacion Inventario': [
    '/ubicacion-inventario'
  ],
  'CRUD Mentores': [
    '/mentors'
  ],
  'CRUD Usuarios': [
    '/users',
    '/register-user',
    '/edit-user/:id'
  ],
  'CRUD Tratamientos': [
    '/tratamientos'
  ],
  'CRUD Medicos': [
    '/medicos'
  ],
  'CRUD Profesiones': [
    '/professions'
  ],
  'CRUD Requisitos': [
    '/requisitos-socia'
  ],
  'CRUD Caracteristicas': [
    '/syndrome-characteristics'
  ],
  'CRUD Formato Carnet': [
    '/formato-carnet'
  ],
  'CRUD Formatos': [
    '/editar-formatos'
  ],
  'CRUD Carnet': [
    '/carnets'
  ],
  'CRUD Especialidad Médico': [
    '/especialidades-medicas'
  ],
  'CRUD Tipo Socia': [
    '/tipo-socia'
  ],
  'CRUD Talla': [
    '/tallas'
  ],
  'CRUD Reportes': [
    '/reports'
  ],
  'Administrador': 'ALL' // Acceso completo a todas las rutas
};

// Mapeo de permisos a elementos del menú
export const PERMISSION_MENU_ITEMS = {
  'CRUD Socias': ['partners'],
  'CRUD Hospitales': ['hospitals'],
  'CRUD Inventario': ['inventory'],
  'CRUD Categoria Inventario': ['categoria-inventario'],
  'CRUD Ubicacion Inventario': ['ubicacion-inventario'],
  'CRUD Mentores': ['mentors'],
  'CRUD Usuarios': ['users'],
  'CRUD Tratamientos': ['tratamientos'],
  'CRUD Medicos': ['medicos'],
  'CRUD Profesiones': ['professions'],
  'CRUD Requisitos': ['requisitos'],
  'CRUD Caracteristicas': ['characteristics'],
  'CRUD Formato Carnet': ['formato-carnet'],
  'CRUD Formatos': ['formato-carnet'],
  'CRUD Carnet': ['carnets'],
  'CRUD Especialidad Médico': ['especialidades'],
  'CRUD Tipo Socia': ['tipo-socia'],
  'CRUD Talla': ['tallas'],
  'CRUD Reportes': ['reports'],
  'Administrador': 'ALL'
};

/**
 * Verifica si el usuario tiene permiso para acceder a una ruta
 * @param {Array} userPermissions - Array de objetos de permisos del usuario
 * @param {String} route - Ruta a verificar
 * @returns {Boolean}
 */
export const hasRoutePermission = (userPermissions, route) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  // Extraer nombres de permisos del array de objetos
  const permissionNames = userPermissions.map(p => 
    p.PERMISO_NOMBRE || p.permiso_nombre || p.nombre || p.name || p
  );

  // Si es administrador, tiene acceso a todo
  if (permissionNames.includes('Administrador')) {
    return true;
  }

  // Normalizar la ruta (remover parámetros dinámicos para comparación)
  const normalizedRoute = route.split('/').slice(0, 3).join('/');

  // Verificar si algún permiso da acceso a esta ruta
  return permissionNames.some(permName => {
    const allowedRoutes = PERMISSION_ROUTES[permName];
    if (allowedRoutes === 'ALL') return true;
    if (!Array.isArray(allowedRoutes)) return false;

    return allowedRoutes.some(allowedRoute => {
      // Comparación exacta o con parámetros
      const normalizedAllowed = allowedRoute.split('/').slice(0, 3).join('/');
      return normalizedRoute === normalizedAllowed || route === allowedRoute;
    });
  });
};

/**
 * Verifica si el usuario tiene un permiso específico
 * @param {Array} userPermissions - Array de objetos de permisos del usuario
 * @param {String} permissionName - Nombre del permiso a verificar
 * @returns {Boolean}
 */
export const hasPermission = (userPermissions, permissionName) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return false;
  }

  const permissionNames = userPermissions.map(p => 
    p.PERMISO_NOMBRE || p.permiso_nombre || p.nombre || p.name || p
  );

  // Si es administrador, tiene todos los permisos
  if (permissionNames.includes('Administrador')) {
    return true;
  }

  return permissionNames.includes(permissionName);
};

/**
 * Filtra los items del menú según los permisos del usuario
 * @param {Array} menuItems - Array de items del menú
 * @param {Array} userPermissions - Array de objetos de permisos del usuario
 * @returns {Array} - Items del menú filtrados
 */
export const filterMenuByPermissions = (menuItems, userPermissions) => {
  if (!userPermissions || !Array.isArray(userPermissions)) {
    return [];
  }

  const permissionNames = userPermissions.map(p => 
    p.PERMISO_NOMBRE || p.permiso_nombre || p.nombre || p.name || p
  );

  // Si es administrador, mostrar todo
  if (permissionNames.includes('Administrador')) {
    return menuItems;
  }

  // Filtrar items según permisos
  return menuItems.filter(item => {
    // Dashboard siempre visible
    if (item.path === '/dashboard') return true;
    // Cambiar contraseña siempre visible
    if (item.path === '/change-password') return true;

    // Verificar si tiene permiso para esta ruta
    return hasRoutePermission(userPermissions, item.path);
  });
};

/**
 * Obtiene todos los permisos del usuario actual
 * @returns {Array} - Array de permisos
 */
export const getCurrentUserPermissions = () => {
  try {
    const userDataStr = localStorage.getItem('fepast_user_data');
    if (!userDataStr) return [];
    
    const userData = JSON.parse(userDataStr);
    return userData.permisos || [];
  } catch (error) {
    console.error('Error obteniendo permisos del usuario:', error);
    return [];
  }
};

/**
 * Verifica si el usuario es administrador
 * @param {Array} userPermissions - Array de permisos del usuario (opcional)
 * @returns {Boolean}
 */
export const isAdmin = (userPermissions = null) => {
  const permisos = userPermissions || getCurrentUserPermissions();
  return hasPermission(permisos, 'Administrador');
};
